/**
 * Apoio do módulo de convênios: rótulos, cores e formatações usadas pelas
 * telas de convênio, carteirinha, guia e lote.
 *
 * Os rótulos ficam aqui, e não em cada página, porque o mesmo status aparece em
 * quatro telas diferentes (lista de guias, lote, detalhe do lote e ficha do
 * paciente) — duplicar o mapa é como as telas passam a divergir entre si.
 */

export const STATUS_GUIA = {
    EMITIDA: { label: 'Emitida', cor: 'default', ajuda: 'Registrada, ainda não enviada ao convênio.' },
    FATURADA: { label: 'Faturada', cor: 'info', ajuda: 'Enviada ao convênio, aguardando retorno.' },
    PAGA: { label: 'Paga', cor: 'success', ajuda: 'Repasse recebido.' },
    GLOSADA: { label: 'Glosada', cor: 'error', ajuda: 'Recusada pelo convênio; pode ser reapresentada.' },
    CANCELADA: { label: 'Cancelada', cor: 'warning', ajuda: 'Emissão desfeita.' },
};

export const STATUS_LOTE = {
    ABERTO: { label: 'Aberto', cor: 'default', ajuda: 'Em montagem: guias podem entrar e sair.' },
    ENVIADO: { label: 'Enviado', cor: 'info', ajuda: 'Entregue à operadora; título de repasse criado.' },
    CONCILIADO: { label: 'Conciliado', cor: 'success', ajuda: 'Retorno aplicado em todas as guias.' },
    CANCELADO: { label: 'Cancelado', cor: 'warning', ajuda: 'Montagem desfeita; guias liberadas.' },
};

/** Transições que a tela oferece, espelhando a máquina de estados do back-end. */
export const TRANSICOES_GUIA = {
    EMITIDA: ['FATURADA', 'CANCELADA'],
    FATURADA: ['PAGA', 'GLOSADA'],
    GLOSADA: ['FATURADA'],
    PAGA: [],
    CANCELADA: [],
};

export const labelStatusGuia = (status) => STATUS_GUIA[status]?.label || status || '-';
export const corStatusGuia = (status) => STATUS_GUIA[status]?.cor || 'default';
export const labelStatusLote = (status) => STATUS_LOTE[status]?.label || status || '-';
export const corStatusLote = (status) => STATUS_LOTE[status]?.cor || 'default';

export const MODALIDADE_LABEL = {
    PRESENCIAL: 'Presencial',
    ONLINE: 'Online',
};

export const somenteDigitos = (valor) => (valor || '').replace(/\D/g, '');

export const formatarMoeda = (valor) => {
    const numero = Number(valor ?? 0);
    if (Number.isNaN(numero)) return 'R$ 0,00';
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const formatarCnpj = (valor) => {
    const d = somenteDigitos(valor).slice(0, 14);
    return d
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
};

export const formatarCep = (valor) => {
    const d = somenteDigitos(valor).slice(0, 8);
    return d.replace(/^(\d{5})(\d)/, '$1-$2');
};

export const formatarTelefone = (valor) => {
    const d = somenteDigitos(valor).slice(0, 11);
    if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};

/** Data ISO (yyyy-MM-dd) para dd/MM/yyyy, sem passar por Date (evita fuso). */
export const formatarData = (iso) => {
    if (!iso) return '-';
    const [ano, mes, dia] = String(iso).substring(0, 10).split('-');
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : iso;
};

/** Primeiro e último dia do mês corrente, em ISO — padrão da competência do lote. */
export const competenciaDoMes = (base = new Date()) => {
    const ano = base.getFullYear();
    const mes = base.getMonth();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { inicio: iso(new Date(ano, mes, 1)), fim: iso(new Date(ano, mes + 1, 0)) };
};
